import { useEffect, useRef, useState } from "react";
import {
  motion,
  useInView,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from "framer-motion";
import Button from "../ui/Button";
import { 
  ArrowLeft02Icon, 
  ArrowRight01Icon,
  UserGroupIcon,
  CheckmarkBadge01Icon,
  Clock01Icon,
  SparklesIcon,
} from "hugeicons-react";
import Magnetic from "../ui/Magnetic";
import SectionBadge from "../ui/SectionBadge";
import { useMobilePerformance, useMobileAnimationConfig } from "../../hooks/useMobilePerformance";

// NEW Premium abstract background
const HERO_BG_URL = "/images/magma-hero-bg.png";

interface HeroProps {
  onOpenBooking: () => void;
}

export default function Hero({ onOpenBooking }: HeroProps) {
  const { prefersReducedMotion, isMobile } = useMobilePerformance();
  const { transition, fadeIn, fadeUp, slideInRight, scaleIn, shouldDisableHeavyEffects } = useMobileAnimationConfig();
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    // Only track mouse on desktop (heavy effect)
    if (shouldDisableHeavyEffects) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const statRef = useRef(null);
  const statsInView = useInView(statRef, { once: true, margin: "0px" });
  const statTarget = 100;
  const statMotion = useMotionValue(0);
  const statValue = useSpring(statMotion, {
    stiffness: isMobile ? 120 : 80,
    damping: isMobile ? 20 : 15,
  });
  const statDisplay = useTransform(statValue, (v) => `${Math.round(v)}`);

  useEffect(() => {
    if (statsInView) {
      statMotion.set(statTarget);
    }
  }, [statsInView, statMotion, statTarget]);

  const handleScrollToPackages = () => {
    const el = document.getElementById("packages");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Parallax Scroll Hooks
  const { scrollY } = useScroll();
  const yText = useTransform(scrollY, [0, 500], [0, 100]);
  const yBackground = useTransform(scrollY, [0, 500], [0, 50]);

  return (
    <section 
      id="home" 
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-20 bg-[#050000]"
    >
      {/* Background Layer */}
      <div className="absolute inset-0 z-0 overflow-hidden bg-black select-none">
        <motion.div
          className="absolute inset-0 pointer-events-none opacity-70"
          style={prefersReducedMotion ? {} : { y: yBackground, scale: 1.1 }}
        >
          <img
            src={HERO_BG_URL}
            alt="Hero Background"
            className="absolute inset-0 w-full h-full object-cover mix-blend-screen"
          />
        </motion.div>

        {/* --- CINEMATIC HUD OVERLAYS (Desktop only for performance) --- */}
        <div className="absolute inset-0 z-20 pointer-events-none hidden md:block">
          {/* Subtle Scanning Line - Desktop only */}
          {!shouldDisableHeavyEffects && (
            <motion.div 
              initial={{ top: "0%" }}
              animate={{ top: "100%" }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/30 to-transparent shadow-[0_0_15px_rgba(220,20,60,0.5)]"
              style={{ willChange: 'top' }}
            />
          )}

          {/* Technical Corner HUDs */}
          <div className="absolute top-10 left-10 w-32 h-32 border-t border-l border-white/20 rounded-tl-3xl opacity-40" />
          <div className="absolute bottom-10 right-10 w-32 h-32 border-b border-r border-white/20 rounded-br-3xl opacity-40" />

          {/* Targeting Crosshair (Center) */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 opacity-10 pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1px] h-4 bg-white" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1px] h-4 bg-white" />
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-[1px] bg-white" />
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-[1px] bg-white" />
            <div className="absolute inset-0 border border-white rounded-full scale-50" />
          </div>

          {/* Dynamic Red Glare - Desktop only */}
          {!shouldDisableHeavyEffects && (
            <motion.div 
              animate={{ opacity: [0.2, 0.4, 0.2] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent mix-blend-overlay"
            />
          )}
        </div>

        {/* Strong Red Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-red-600/40 z-10" />
        <div className="absolute inset-0 bg-red-600/10 mix-blend-overlay z-10" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_right,rgba(255,0,0,0.2)_0%,transparent_70%)] z-10" />
        <div className="absolute inset-0 bg-red-900/5 z-10 pointer-events-none" />

        {/* Dynamic Light Scan (Mouse Follow) */}
        {!shouldDisableHeavyEffects && (
          <motion.div
            className="absolute inset-0 z-10 pointer-events-none opacity-40 mix-blend-soft-light"
            animate={{
              background: `radial-gradient(circle 500px at ${mousePos.x}px ${mousePos.y}px, rgba(220,20,60,0.3), transparent 70%)`,
            }}
          />
        )}

        {/* Ambient Glows */}
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-red-900/10 via-transparent to-[#050000] z-10 pointer-events-none" />
        
        {/* Floating Abstract Shapes for Depth - Desktop only, reduced blur for perf */}
        {!shouldDisableHeavyEffects && (
          <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none hidden md:block">
            <motion.div
              animate={{ 
                y: [0, -40, 0],
                scale: [1, 1.05, 1]
              }}
              transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-[10%] -left-20 w-[600px] h-[600px] bg-primary/5 blur-[80px] rounded-full pointer-events-none"
              style={{ willChange: 'transform' }}
            />
            <motion.div
              animate={{ 
                y: [0, 60, 0],
                x: [0, 20, 0]
              }}
              transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
              className="absolute bottom-[20%] -right-20 w-[500px] h-[500px] bg-red-600/5 blur-[80px] rounded-full pointer-events-none"
              style={{ willChange: 'transform' }}
            />
          </div>
        )}

        {/* Animated Particles/Sparks Effect - Desktop only, reduced count for performance */}
        {!shouldDisableHeavyEffects && (
           <div className="absolute inset-0 z-10 opacity-40 pointer-events-none hidden md:block">
              {[...Array(10)].map((_, i) => (
                 <motion.div
                   key={i}
                   className="absolute w-1 h-1 bg-primary rounded-full"
                   style={{
                     left: `${10 + (i * 8)}%`,
                     top: `${20 + (i * 6)}%`,
                     willChange: 'transform, opacity',
                   }}
                   animate={{
                     y: [0, -100],
                     opacity: [0, 0.8, 0],
                   }}
                   transition={{
                     duration: 5 + i,
                     repeat: Infinity,
                     delay: i * 0.5,
                     ease: "easeOut",
                   }}
                 />
              ))}
           </div>
        )}

        {/* Transition Mask */}
        <div className="absolute bottom-0 left-0 right-0 h-24 md:h-32 bg-gradient-to-t from-[#050000] via-[#050000]/80 to-transparent z-20 pointer-events-none" />
      </div>

      <div className="container mx-auto px-4 lg:px-8 z-30 relative h-full flex flex-col justify-center" dir="rtl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center w-full">
          
          {/* Right Column: Text Content (Main Message) */}
          <div className="order-1 lg:order-1 max-w-4xl mr-0 ml-auto xl:mr-12">
            <motion.div style={prefersReducedMotion ? {} : { y: yText }}>
            {/* Staggered entry for text elements */}
            <motion.div
              initial={fadeIn.initial}
              animate={fadeIn.animate}
              transition={{ ...transition, delay: 0.1 }}
              className="flex justify-start mb-6"
            >
              <SectionBadge text="الوجهة الأولى للعناية الفاخرة بالسيارات" icon={SparklesIcon} variant="ghost" showDot={true} />
            </motion.div>

            {/* Main Title Group */}
            <div className="relative mb-8 text-right overflow-hidden">
                <motion.h1
                  initial={slideInRight.initial}
                  animate={slideInRight.animate}
                  transition={{ ...transition, delay: 0.15 }}
                  className="text-5xl sm:text-6xl md:text-7xl lg:text-7xl xl:text-8xl font-black leading-[1.1] text-white tracking-tighter"
                >
                  <motion.span 
                    initial={{ opacity: 0, x: isMobile ? 10 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: isMobile ? 0.2 : 0.3, duration: isMobile ? 0.3 : 1 }}
                    className="block mb-2"
                  >
                    دلع
                  </motion.span>
                  <motion.span 
                    initial={{ opacity: 0, x: isMobile ? 15 : 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: isMobile ? 0.3 : 0.5, duration: isMobile ? 0.3 : 1 }}
                    className="relative inline-block text-transparent bg-clip-text bg-gradient-to-br from-white via-white to-white/40"
                  >
                    سيارتك
                  </motion.span>
                </motion.h1>

                <div className="mt-4 flex flex-col items-start">
                   <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: isMobile ? "40%" : "35%" }}
                    transition={{ duration: isMobile ? 0.5 : 1.2, delay: isMobile ? 0.4 : 1.2, ease: "easeOut" }}
                    className="h-1 bg-primary mb-2 md:mb-4 rounded-full shadow-[0_0_25px_rgba(220,20,60,0.8)]"
                   />
                   <motion.p
                    initial={fadeIn.initial}
                    animate={fadeIn.animate}
                    transition={{ ...transition, delay: isMobile ? 0.35 : 0.8 }}
                     className="text-primary-neon font-black text-2xl md:text-3xl lg:text-4xl uppercase tracking-widest mb-4 md:mb-6 text-right"
                   >
                     بأعلى جودة
                   </motion.p>
                </div>
            </div>

            <motion.p
              initial={fadeUp.initial}
              animate={fadeUp.animate}
              transition={{ ...(fadeUp.transition || transition), delay: isMobile ? 0.5 : 1 }}
              className="text-base md:text-lg lg:text-xl text-gray-400 mb-10 max-w-2xl leading-relaxed font-light text-right"
            >
              نقدم خدمات <motion.span 
                initial={{ color: "#9ca3af" }}
                animate={{ color: "#ffffff" }}
                transition={{ delay: isMobile ? 1 : 2, duration: isMobile ? 0.8 : 1.5 }}
                className="font-medium italic underline decoration-primary underline-offset-8"
              >تلميع وحماية</motion.span> السيارات بأحدث التقنيات الألمانية والمنتجات الفاخرة.
            </motion.p>

            <motion.div
              initial={scaleIn.initial}
              animate={scaleIn.animate}
              transition={{ ...(scaleIn.transition || transition), delay: isMobile ? 0.6 : 1.3 }}
              className="flex flex-col sm:flex-row gap-4 md:gap-6 mb-16"
            >
              <Magnetic strength={0.3}>
                <Button
                  size="xl"
                  className="w-full sm:w-auto px-12 py-6 text-xl font-black bg-primary hover:bg-white hover:text-primary shadow-[0_0_50px_rgba(255,0,0,0.3)] transition-all duration-500 overflow-hidden group"
                  onClick={() => onOpenBooking()}
                >
                  <span className="relative z-10 flex items-center justify-center gap-4">
                    ابدأ تجربتك الآن
                    <ArrowLeft02Icon size={28} />
                  </span>
                </Button>
              </Magnetic>

              <Magnetic strength={0.3}>
                <button
                  className="w-full sm:w-auto px-12 py-6 text-xl font-bold text-white/60 hover:text-white transition-all flex items-center justify-center gap-4 group"
                  onClick={handleScrollToPackages}
                >
                  <ArrowRight01Icon
                    size={28}
                    className="group-hover:-translate-x-2 transition-transform text-white/40"
                  />
                  <span>استكشف خدماتنا</span>
                </button>
              </Magnetic>
            </motion.div>

            {/* Premium Stats Bento-Style - MOBILE OPTIMIZED */}
            <motion.div
              initial={fadeUp.initial}
              animate={fadeUp.animate}
              transition={{ ...(fadeUp.transition || transition), delay: isMobile ? 0.3 : 1.6 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 w-full max-w-4xl"
              ref={statRef}
            >
              {[
                { label: "عميل سعيد", value: <motion.span>{statDisplay}</motion.span>, suffix: "+", icon: UserGroupIcon, desc: "أعلى تقييم" },
                { label: "سنوات ضمان", value: "6", suffix: "", icon: CheckmarkBadge01Icon, desc: "حماية تامة" },
                { label: "تغطية كاملة", value: "24/7", suffix: "", icon: Clock01Icon, desc: "دعم فني" }
              ].map((stat, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: isMobile ? 15 : 20 }}
                  animate={statsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ 
                    delay: isMobile ? (0.2 + idx * 0.08) : (1.8 + idx * 0.1), 
                    duration: isMobile ? 0.4 : 0.8,
                    ease: isMobile ? [0.32, 0.72, 0, 1] : undefined
                  }}
                  className={`group relative p-4 md:p-5 lg:p-6 rounded-3xl bg-white/[0.03] border border-white/[0.06] overflow-hidden transition-colors duration-300
                    ${isMobile ? '' : 'backdrop-blur-xl hover:bg-white/[0.06] hover:border-primary/30'}`}
                >
                  {/* Accent glow - simplified on mobile, no blur */} 
                  {!isMobile && (
                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 blur-[40px] -mr-16 -mt-16 group-hover:bg-primary/20 transition-all duration-700" />
                  )}
                  <div className="relative z-10">
                    <stat.icon className={`text-primary size-7 md:size-8 mb-4 md:mb-6 ${isMobile ? '' : 'group-hover:scale-110 transition-transform duration-500'}`} />
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-2xl md:text-3xl lg:text-3xl xl:text-4xl font-black text-white">{stat.value}</span>
                      <span className="text-primary text-xl md:text-2xl font-black">{stat.suffix}</span>
                    </div>
                    <div className="text-base md:text-lg font-bold text-white mb-1">{stat.label}</div>
                    <div className="text-xs md:text-sm text-gray-500">{stat.desc}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
            </motion.div>
          </div>

          {/* Left Column: EMPTY AS REQUESTED */}
          <div className="order-2 lg:order-2 relative h-[80vh] hidden lg:flex items-center justify-center pointer-events-none" />
        </div>
      </div>
      {/* Bottom Seamless Transition Mask */}
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#020000] via-[#020000]/50 to-transparent z-40 pointer-events-none" />
    </section>
  );
}

// Sub-component for the Floating Cards (CLEANED UP/REMOVED SINCE WE USE FLUX CORE)
